import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, forwardRef } from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { NzTreeNodeOptions } from "ng-zorro-antd/tree";
import { Observable, forkJoin, map, of, takeUntil } from "rxjs";
import { localize } from "@gtmhub/localization";
import { ISessionsStoreState } from "@gtmhub/sessions/redux/session-reducer";
import { reduxStoreContainer } from "@gtmhub/state-management/state-management.module";
import { ITeamsStoreState } from "@gtmhub/teams";
import { IdMap, unique } from "@gtmhub/util";
import { AnalyticsService } from "@webapp/analytics/services/analytics.service";
import { DestroyService } from "@webapp/core/util/services/destroy.service";
import { FeatureFlag } from "@webapp/feature-toggles/models/feature-toggles.models";
import { FeatureTogglesFacade } from "@webapp/feature-toggles/services/feature-toggles-facade.service";
import { Metric } from "@webapp/okrs/metrics/models/metric.models";
import {
  IGetSearchParams,
  IParentSearchResult,
  IParentSelectionTeam,
  IParentSelector,
  IParentSelectorEntry,
  IParentSelectorNode,
  ParentSelectorSuggestedListType,
  ParentSelectorSuggestionsLoadedType,
  ParentSelectorTrackingPayloadConverted,
  ParentSelectorTrackingPayloadMeta,
} from "@webapp/okrs/models/parent-selector.models";
import { ParentSelectorFacade } from "@webapp/okrs/services/parent-selector-facade.service";
import { SuggestionsPayload, SuggestionsResponse } from "@webapp/okrs/suggestions/models/suggestions.models";
import { SuggestionsFacade } from "@webapp/okrs/suggestions/services/suggestions-facade.service";
import { PARENT_OKR_SEARCH_EVENT, PARENT_OKR_SUGGESTION_EVENT, convertObjKeysFromCamelcaseToUnderscoreCase } from "@webapp/okrs/utils/utils";
import { Session } from "@webapp/sessions/models/sessions.model";
import { IMultiSelectorGoal, IMultiSelectorMetric, IMultiSelectorTrackingParams } from "@webapp/shared/components/multi-selector/multi-selector.models";

const PARENT_SELECTOR_CONTROL_VALUE_ACCESSOR = { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => ParentSelectorComponent), multi: true };

@Component({
  selector: "parent-selector",
  templateUrl: "parent-selector.component.html",
  changeDetection: ChangeDetectionStrategy.Default,
  providers: [DestroyService, PARENT_SELECTOR_CONTROL_VALUE_ACCESSOR],
})
export class ParentSelectorComponent implements OnInit, OnChanges, ControlValueAccessor {
  @Input() public selectedParentItem: Partial<IParentSelectorEntry>;
  @Input() public goalIdToExclude: string;
  @Input() public metricsToExclude: Metric[];
  @Input() public suggestionTarget: string;
  @Input() public ownerIds: string[];
  @Input() public sessionIds: string[];
  @Input() public uiId: string;
  @Input() public readonly: boolean;
  @Input() public disabled: boolean;
  @Input() public required: boolean;
  @Input() public borderless: boolean;
  @Input() public placeholder: string;
  @Input() public location: string;
  @Input() public a11yLabelledby: string;
  @Input() public a11yRequired = false;
  @Input() public a11yDisabled = false;
  @Output() public readonly selectionChange = new EventEmitter<IParentSelector>();

  private inputValue = "";
  private isEmptyInputValue = true;
  private canPerformSearch = false;
  public nodesLoading: boolean;
  public nodes: IParentSelectorNode[] = [];
  public isSmartAlignmentAvailable: boolean;
  private goals: IMultiSelectorGoal[] = [];
  private sessionsMap: IdMap<Session> = {};
  private teams: IParentSelectionTeam[] = [];
  private suggestionResults: IParentSelectorNode[] = [];
  private suggestionsLoadedType: ParentSelectorSuggestionsLoadedType;
  private scrollUsed = false;

  public searchFn: (searchTerm: string) => Observable<IParentSearchResult[]>;
  public sortFn: (itemA: IMultiSelectorGoal | IMultiSelectorMetric, itemB: IMultiSelectorGoal | IMultiSelectorMetric) => number;

  private notifyControlChange: (value: IParentSelector) => void;

  constructor(
    private parentSelectorFacade: ParentSelectorFacade,
    private suggestionsFacade: SuggestionsFacade,
    private destroyService$: DestroyService,
    private analyticsService: AnalyticsService,
    private featureTogglesFacade: FeatureTogglesFacade,
    private changeDetector: ChangeDetectorRef
  ) {}

  public ngOnInit(): void {
    this.sortFn = this.createSortFunction();
    this.searchFn = this.createSearchFunc();

    this.getSessions();

    this.sessionIds = this.getSessionIdsToFilterBy();

    this.featureTogglesFacade
      .isFeatureAvailable$(FeatureFlag.FeatureSmartAlignment)
      .pipe(takeUntil(this.destroyService$))
      .subscribe((isSmartAlignmentAvailable) => {
        this.isSmartAlignmentAvailable = isSmartAlignmentAvailable;
        this.getParentSelectionItems();
      });

    this.getTeams();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    this.sessionIds = this.getSessionIdsToFilterBy();

    if (this.hasGoalChanges(changes)) {
      this.getParentSelectionItems();
    }
  }

  public writeValue(value: IParentSelector): void {
    this.selectedParentItem = value;
  }

  public registerOnChange(fn: (value: IParentSelector) => void): void {
    this.notifyControlChange = fn;
  }

  public registerOnTouched(): void {
    // required by the ControlValueAccessor interface
  }

  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  public createSearchFunc(): (searchTerm: string) => Observable<IParentSearchResult[]> {
    return (searchTerm: string) => {
      const config: IGetSearchParams = { searchTerm, sessionIds: this.sessionIds, goalIdToExclude: this.goalIdToExclude };
      return this.parentSelectorFacade.getParentSelectionItemsBySearchTerm$(config);
    };
  }

  public createSortFunction(): (itemA: IMultiSelectorGoal | IMultiSelectorMetric, itemB: IMultiSelectorGoal | IMultiSelectorMetric) => number {
    return (itemA: IMultiSelectorGoal | IMultiSelectorMetric, itemB: IMultiSelectorGoal | IMultiSelectorMetric) => {
      return this.orderParentSelectionItemsByOwnersType(itemA, itemB);
    };
  }

  public onOpenChange(isOpen: boolean): void {
    if (isOpen) {
      this.getParentSelectionItems();
    }
  }

  public scrollIsUsed(scrollUsed: boolean): void {
    this.scrollUsed = scrollUsed;
  }

  private hasGoalChanges({ sessionIds, ownerIds }: SimpleChanges): boolean {
    return this.isSessionChanged({ sessionIds }) || this.isOwnerChanged({ ownerIds });
  }

  private isSessionChanged({ sessionIds }: SimpleChanges): boolean {
    return sessionIds?.currentValue?.length > 0 && sessionIds?.currentValue !== sessionIds?.previousValue && !sessionIds?.isFirstChange();
  }

  private isOwnerChanged({ ownerIds }: SimpleChanges): boolean {
    return ownerIds?.currentValue?.length > 0 && ownerIds?.currentValue !== ownerIds?.previousValue && !ownerIds?.isFirstChange();
  }

  private getSessions(): void {
    this.sessionsMap = reduxStoreContainer.reduxStore.getState<ISessionsStoreState>().sessions.map;
  }

  private getTeams(): void {
    this.teams = reduxStoreContainer.reduxStore.getState<ITeamsStoreState>().teams.items;
  }

  public setTrackingParams({ canPerformSearch, isEmptyInputValue, inputValue }: IMultiSelectorTrackingParams): void {
    this.canPerformSearch = canPerformSearch;
    this.isEmptyInputValue = isEmptyInputValue;
    this.inputValue = inputValue;
  }

  public updateValue(selectedNode: NzTreeNodeOptions): void {
    const nodeToUpdate = selectedNode ? { id: selectedNode.key, type: selectedNode.type } : null;

    this.selectionChange.emit(nodeToUpdate);
    this.notifyControlChange?.(nodeToUpdate);

    if (selectedNode && this.isSmartAlignmentAvailable) {
      const payload = this.buildTrackingPayload(selectedNode);
      if (payload) {
        this.analyticsService.track(payload.event, payload.meta);
      }
    }
  }

  private getParentSelectionItems(): void {
    this.nodesLoading = true;
    if (this.isSmartAlignmentAvailable) {
      this.getParentSelectionItemsWithSmartAlignmentEnabled();
    } else {
      this.parentSelectorFacade
        .getParentSelectionItems$({
          sessionIds: this.sessionIds,
          ownerIds: this.getOwnerIdsToFilterBy(),
          goalIdToExclude: this.goalIdToExclude,
        })
        .pipe(
          takeUntil(this.destroyService$),
          map((goals) => this.getSortedGoals(goals))
        )
        .subscribe((goals) => {
          this.goals = goals;

          this.buildTreeNodes();
          this.nodesLoading = false;

          this.changeDetector.markForCheck();
        });
    }
  }

  private getParentSelectionItemsWithSmartAlignmentEnabled(): void {
    const excludedIds = [...(this.metricsToExclude?.map((metric) => metric.id) || []), ...(this.goalIdToExclude ? [this.goalIdToExclude] : [])];

    const payload: SuggestionsPayload = {
      sessionId: this.sessionIds[0],
      name: this.suggestionTarget,
      limit: 10,
      excludedIds,
    };

    const suggestionsRequest = payload.name && payload.sessionId ? this.suggestionsFacade.getSuggestions$(payload).pipe(takeUntil(this.destroyService$)) : of([]);

    forkJoin([
      suggestionsRequest,
      this.parentSelectorFacade
        .getParentSelectionItems$({
          sessionIds: this.sessionIds,
          ownerIds: this.getOwnerIdsToFilterBy(),
          goalIdToExclude: this.goalIdToExclude,
        })
        .pipe(
          takeUntil(this.destroyService$),
          map((goals) => this.getSortedGoals(goals))
        ),
    ])
      .pipe(takeUntil(this.destroyService$))
      .subscribe((data) => {
        this.goals = data[1];
        if (data[0]?.length) {
          this.suggestionResults = this.buildSuggestionsList(data[0]);
        } else {
          this.suggestionResults = [];
        }

        this.buildTreeNodes();
        this.nodesLoading = false;

        const trackingPayload = {
          location: this.location || "none",
          suggestions_loaded: this.suggestionsLoadedType,
          suggestions_selected: "no",
          search_performed: "no",
        };

        this.analyticsService.track(PARENT_OKR_SUGGESTION_EVENT, trackingPayload);
      });
  }

  private getSortedGoals(goals: IMultiSelectorGoal[]): IMultiSelectorGoal[] {
    const sortedGoals = goals.sort((goalA, goalB) => this.orderParentSelectionItemsByOwnersType(goalA, goalB));
    return sortedGoals.map((goal) => this.getSortedGoalMetrics(goal));
  }

  private getSortedGoalMetrics(goal: IMultiSelectorGoal): IMultiSelectorGoal {
    const sortedMetrics = goal?.metrics?.sort((metricA, metricB) => this.orderParentSelectionItemsByOwnersType(metricA, metricB));
    return { ...goal, metrics: sortedMetrics };
  }

  private getSessionIdsToFilterBy(): string[] {
    return this.sessionIds?.filter(Boolean);
  }

  private getOwnerIdsToFilterBy(): string[] {
    return unique(
      (this.ownerIds || []).reduce((ownerIdsToFilterBy, id) => {
        const team = this.getTeamById(id);
        const parentTeam = team ? this.getTeamById(team.parentId) : null;
        ownerIdsToFilterBy.push(id);

        if (team && parentTeam) {
          ownerIdsToFilterBy.push(parentTeam.id, parentTeam.manager);
        } else if (!team) {
          ownerIdsToFilterBy.push(...this.getIdsRelatedToOwnerId(id));
        }

        return ownerIdsToFilterBy;
      }, [])
    ).filter(Boolean);
  }

  private getTeamById(id: string): IParentSelectionTeam {
    return this.teams.find((team) => team.id === id);
  }

  private getIdsRelatedToOwnerId(ownerId: string): string[] {
    return this.getTeamsByManagerOrMembersId(ownerId).reduce((ids, { id, manager, parentId }) => {
      ids.push(id, manager);

      const parentTeam = this.getTeamById(parentId);

      if (parentTeam) {
        ids.push(parentTeam.id, parentTeam.manager);
      }

      return ids;
    }, []);
  }

  private getTeamsByManagerOrMembersId(id: string): IParentSelectionTeam[] {
    return this.teams?.filter(({ manager, members }) => id === manager || members?.some((member) => id === member));
  }

  private orderParentSelectionItemsByOwnersType(itemA: IMultiSelectorGoal | IMultiSelectorMetric, itemB: IMultiSelectorGoal | IMultiSelectorMetric): number {
    const ownerIdsA = this.getOwnerIdsIncludedInOwnerIdsToFilterBy(itemA.ownerIds);
    const ownerIdsB = this.getOwnerIdsIncludedInOwnerIdsToFilterBy(itemB.ownerIds);

    if (ownerIdsA.length === ownerIdsB.length) {
      return this.getSortOrderWeight(ownerIdsA) - this.getSortOrderWeight(ownerIdsB);
    } else {
      return ownerIdsB.length - ownerIdsA.length;
    }
  }

  private getOwnerIdsIncludedInOwnerIdsToFilterBy(ownerIds: string[]): string[] {
    const ownersToFilterBy = this.getOwnerIdsToFilterBy();
    return ownerIds.filter((id) => ownersToFilterBy.includes(id));
  }

  private getSortOrderWeight(ownerIds: string[]): number {
    const ownersToFilterBy = this.getOwnerIdsToFilterBy();
    return ownerIds.reduce((sortWeight, ownerId) => sortWeight + ownersToFilterBy.indexOf(ownerId), 0);
  }

  private buildTreeNodes(): void {
    let hasExpandedGoal = false;

    const nodes: IParentSelectorNode[] = this.sessionIds.reduce((sessionNodes, sessionId) => {
      const goalNodes = this.getGoalsBySessionId(sessionId).map((goal) => {
        const goalNode = this.buildTreeGoalNode({ goal, hasExpandedGoal });

        if (goalNode.expanded) {
          hasExpandedGoal = true;
        }

        return goalNode;
      });

      if (goalNodes.length > 0) {
        sessionNodes.push(this.buildSessionNode({ sessionId, goalNodes }));
      }

      return sessionNodes;
    }, []);

    if (this.isSmartAlignmentAvailable) {
      if (this.suggestionResults.length) {
        this.nodes = [...this.suggestionResults, ...this.excludeSuggestedFromRelatedGoals(this.suggestionResults[0].children, nodes)];
        this.suggestionsLoadedType = this.getSuggestionsLoadedType(this.nodes);
      } else {
        this.nodes = nodes;
        this.suggestionsLoadedType = "related";
      }
    } else {
      this.nodes = nodes;
    }
  }

  private excludeSuggestedFromRelatedGoals(suggestedChildren: NzTreeNodeOptions[], relatedNodes: IParentSelectorNode[]): IParentSelectorNode[] {
    return relatedNodes.reduce((reduced, node) => {
      node.children = node.children?.filter((nodeChild) =>
        suggestedChildren.every((suggestedChild) => nodeChild.key !== suggestedChild.key || nodeChild.type === "metric")
      );

      let nodeChildsChildrenLength = 0;
      node.children?.forEach((child) => {
        nodeChildsChildrenLength += child.children?.length || 0;
      });

      const totalLength = node.children?.length + nodeChildsChildrenLength;

      if (totalLength) {
        node.title = `${totalLength} ${localize("related_to_you_in").toUpperCase()} ${this.sessionsMap[node.key]?.title.toUpperCase()}`;
        return [...reduced, node];
      }

      return reduced;
    }, []);
  }

  private getGoalsBySessionId(sessionId: string): IMultiSelectorGoal[] {
    return this.goals.filter((goal) => goal.sessionId === sessionId);
  }

  private buildTreeGoalNode({ goal, hasExpandedGoal }: { goal: IMultiSelectorGoal; hasExpandedGoal: boolean }): IParentSelectorNode {
    const goalNode = this.buildGoalNode(goal);
    goalNode.expanded = !hasExpandedGoal && this.canExpandGoalNode(goalNode);

    return goalNode;
  }

  private canExpandGoalNode(goalNode: IParentSelectorNode): boolean {
    const hasSelectedParentItem = Boolean(this.selectedParentItem?.id);
    const hasChildren = Boolean(goalNode.children);
    const hasSelectedMetric = goalNode.children?.some((metric) => metric.key === this.selectedParentItem?.id);

    return (!hasSelectedParentItem && hasChildren) || hasSelectedMetric;
  }

  private buildSessionNode({ sessionId, goalNodes }: { sessionId: string; goalNodes: IParentSelectorNode[] }): IParentSelectorNode {
    const sessionTitle = this.sessionsMap[sessionId].title.toUpperCase();
    let suggestedGoals: string;
    let title: string;

    if (this.isSmartAlignmentAvailable) {
      const counter = goalNodes.length + goalNodes.reduce((total, current) => total + (current.children?.length || 0), 0);
      suggestedGoals = localize("related_to_you_in").toUpperCase();
      title = `${counter} ${suggestedGoals} ${sessionTitle}`;
    } else {
      suggestedGoals = localize("suggested_okrs").toUpperCase();
      title = `${suggestedGoals} - ${sessionTitle}`;
    }

    return {
      key: sessionId,
      title: title,
      ownerIds: [],
      selectable: false,
      expanded: true,
      children: goalNodes,
      hideExpand: true,
      isSessionTitle: true,
    };
  }

  private buildSuggestionsList(nodes: SuggestionsResponse[]): IParentSelectorNode[] {
    const suggestedNodes: IParentSelectorNode[] = [
      {
        key: "",
        title: `${nodes.length} ${localize("similar_objectives_and_key_results")}`.toUpperCase(),
        type: "goal",
        ownerIds: [],
        selectable: false,
        expanded: true,
        hideExpand: true,
        isSessionTitle: true,
        children: nodes
          .map((child) => {
            return {
              key: child.id,
              type: child.type,
              title: child.name,
              relevance: child.relevance,
              ownerIds: child.ownersIds,
              icon: child.type,
              hideExpand: true,
              isLeaf: child.type === "metric",
              ...(child.type === "goal" && { isGoal: true, sessionId: this.sessionIds[0] }),
            };
          })
          .sort((a, b) => b.relevance - a.relevance),
      },
    ];

    return suggestedNodes;
  }

  private buildGoalNode(goal: IMultiSelectorGoal): IParentSelectorNode {
    const GOAL = "goal";

    return {
      key: goal.id,
      title: goal.name,
      isLeaf: this.isLeafGoal(goal),
      children: goal?.metrics?.map((metric) => this.buildMetricNode(metric)),
      icon: GOAL,
      sessionId: goal.sessionId,
      ownerIds: goal.ownerIds,
      type: GOAL,
      isGoal: true,
    };
  }

  private isLeafGoal(goal: IMultiSelectorGoal): boolean {
    return Boolean(!goal?.metrics || goal?.metrics?.length === 0);
  }

  private buildMetricNode(metric: IMultiSelectorMetric): IParentSelectorNode {
    const METRIC = "metric";

    return {
      key: metric.id,
      title: metric.name,
      isLeaf: true,
      icon: METRIC,
      ownerIds: metric.ownerIds,
      type: METRIC,
    };
  }

  private buildTrackingPayload(selectedNode: IParentSelectorNode): ParentSelectorTrackingPayloadConverted {
    const trackingPayloadMeta: ParentSelectorTrackingPayloadMeta = this.buildInitialTrackingPayloadMeta(selectedNode);

    this.scrollUsed = false;

    if (!this.canPerformSearch) {
      if (this.nodes[ParentSelectorSuggestedListType.SIMILAR].children.find((node) => node.key === selectedNode.key)) {
        return this.buildSuggestedListPayload(trackingPayloadMeta, selectedNode, ParentSelectorSuggestedListType.SIMILAR);
      }
      if (this.nodes[ParentSelectorSuggestedListType.RELATED].children.find((node) => node.key === selectedNode.key)) {
        return this.buildSuggestedListPayload(trackingPayloadMeta, selectedNode, ParentSelectorSuggestedListType.RELATED);
      }
    } else if (!this.isEmptyInputValue && this.nodes.find((node) => node.key === selectedNode.key)) {
      return this.buildSearchListPayload(trackingPayloadMeta, selectedNode);
    }

    return null;
  }

  private buildSuggestedListPayload(
    trackingPayloadMeta: ParentSelectorTrackingPayloadMeta,
    selectedNode: IParentSelectorNode,
    listType: ParentSelectorSuggestedListType
  ): ParentSelectorTrackingPayloadConverted {
    return {
      event: PARENT_OKR_SUGGESTION_EVENT,
      meta: convertObjKeysFromCamelcaseToUnderscoreCase({
        ...trackingPayloadMeta,
        suggestionSelectedRank: this.nodes[listType].children.map((node) => node.key).indexOf(selectedNode.key) + 1,
      }),
    };
  }

  private buildSearchListPayload(trackingPayloadMeta: ParentSelectorTrackingPayloadMeta, selectedNode: IParentSelectorNode): ParentSelectorTrackingPayloadConverted {
    return {
      event: PARENT_OKR_SEARCH_EVENT,
      meta: convertObjKeysFromCamelcaseToUnderscoreCase({
        ...trackingPayloadMeta,
        suggestionsSelected: "no",
        searchResultSelectedRank: this.nodes.map((node) => node.key).indexOf(selectedNode.key) + 1,
        searchQuery: this.inputValue,
        searchPerformed: "yes",
        searchResultSelected: "selected",
      }),
    };
  }

  private buildInitialTrackingPayloadMeta(selectedNode: IParentSelectorNode): ParentSelectorTrackingPayloadMeta {
    return {
      location: this.location || "none",
      parentId: selectedNode.key,
      parentType: selectedNode.type,
      relevance: (this.suggestionResults?.length && this.suggestionResults[0]?.children.find((child) => selectedNode.key === child.key)?.relevance) || 0,
      suggestionsLoaded: this.suggestionsLoadedType,
      suggestionsSelected: "yes",
      searchPerformed: "no",
      searchResultSelected: "not selected",
      scrollUsed: this.scrollUsed ? "yes" : "no",
    };
  }

  private getSuggestionsLoadedType(nodes: IParentSelectorNode[]): ParentSelectorSuggestionsLoadedType {
    if (nodes.length > 1) {
      return "related&similar";
    }

    if (nodes.length === 1 && nodes[0].title.toLowerCase().includes("similar")) {
      return "similar";
    }

    if (nodes.length === 1 && nodes[0].title.toLowerCase().includes("related")) {
      return "related";
    }

    return "none";
  }
}
